import { useMemo } from 'react';
import { useTenantMapData } from './useTenantMapData';
import { useWatchlistMapData } from './useWatchlistMapData';
import {
  mergeMapMarkers,
  mergeMapRoutes,
  computeMapBounds,
  buildTenantNetworkRoutes,
  enrichMarkersWithSupplierRisk,
} from '../utils/tenantMapModel';

/**
 * Bird's-eye map for the Watchlist page: tenant supply chain (facilities, suppliers, routes)
 * merged with any explicitly watched companies.
 */
export function useWatchlistPageMapData({
  watchlist = [],
  tenantNodesByKey = {},
  tenantSuppliers = [],
  tenantCompanyRoutes = {},
  tenantProfile,
  companies = [],
  mapRiskAnalyses = {},
  routeAnalyses = {},
  mapRefreshTrigger = 0,
}) {
  const tenantMap = useTenantMapData(tenantNodesByKey, {
    companyRoutes: tenantCompanyRoutes,
    enableGeocoding: false,
    enableSupplierRoutes: false,
  });

  const watchlistMap = useWatchlistMapData(watchlist, {
    riskAnalyses: mapRiskAnalyses,
    routeAnalyses,
    refreshTrigger: mapRefreshTrigger,
  });

  const companyName = tenantProfile?.name || '';

  const tenantMarkers = useMemo(
    () => enrichMarkersWithSupplierRisk(tenantMap.markers, tenantSuppliers, companies),
    [tenantMap.markers, tenantSuppliers, companies]
  );

  const tenantNetworkRoutes = useMemo(
    () => buildTenantNetworkRoutes(tenantMarkers, companyName),
    [tenantMarkers, companyName]
  );

  const markers = useMemo(
    () => mergeMapMarkers(tenantMarkers, watchlistMap.markers),
    [tenantMarkers, watchlistMap.markers]
  );

  const routes = useMemo(
    () => mergeMapRoutes(tenantMap.routes, tenantNetworkRoutes, watchlistMap.routes),
    [tenantMap.routes, tenantNetworkRoutes, watchlistMap.routes]
  );

  const bounds = useMemo(
    () => computeMapBounds(markers, routes) || watchlistMap.bounds || tenantMap.bounds,
    [markers, routes, watchlistMap.bounds, tenantMap.bounds]
  );

  const geocoding = watchlistMap.geocoding || tenantMap.geocoding;
  const hasMapContent = markers.length > 0 || routes.length > 0;

  return {
    markers,
    routes,
    bounds,
    geocoding,
    hasMapContent,
  };
}
